import { Check } from 'lucide-react';

/**
 * MacroBar — reusable bar used by Home (Today's Progress) and Weekly Overview.
 *
 * mode:
 *   'progress' → intake tracking. Full bar shows the full scale; MIN/GOAL/MAX ticks
 *                are rendered, fill = 0 → marker position, color is range-aware.
 *   'plan'     → plan comparison. Full bar = daily goal (100%); fill = planned value
 *                as a percent of the goal (capped at 120%). No MIN/GOAL/MAX ticks.
 *                Status wording: "Aligned with goal" / "Slightly high" / "Slightly low".
 */

// Fixed visual layout for progress mode: zone closer to the right, smaller
var ZONE_L = 55;
var ZONE_W = 28;
var ZONE_R = ZONE_L + ZONE_W; // 83

function computeProgress(current, rangeMin, rangeMax) {
  var isBelow = current < rangeMin;
  var isWithin = current >= rangeMin && current <= rangeMax;
  var isAbove = current > rangeMax;

  var markerPct;
  if (isBelow) {
    var r = rangeMin > 0 ? current / rangeMin : 0;
    markerPct = r * ZONE_L;
  } else if (isWithin) {
    var span = rangeMax - rangeMin;
    var wr = span > 0 ? (current - rangeMin) / span : 0.5;
    markerPct = ZONE_L + wr * ZONE_W;
  } else {
    var ceil = rangeMax * 1.5;
    var or2 = Math.min((current - rangeMax) / (ceil - rangeMax), 1);
    markerPct = ZONE_R + or2 * (100 - ZONE_R);
  }
  markerPct = Math.max(0, Math.min(markerPct, 100));

  return { isBelow: isBelow, isWithin: isWithin, isAbove: isAbove, markerPct: markerPct };
}

export function MacroBar(props) {
  var mode = props.mode || 'progress';
  var label = props.label;
  var macroKey = props.macroKey;
  var unit = props.unit || '';

  if (mode === 'plan') {
    // Plan mode — planned value vs daily goal
    var goalVal = props.goalVal || 0;
    var plannedAvg = Math.round(props.plannedAvg || 0);
    var delta = plannedAvg - Math.round(goalVal);
    var pct = goalVal > 0 ? (plannedAvg / goalVal) * 100 : 0;
    // Alignment threshold: within ±10% of goal
    var aligned = Math.abs(pct - 100) <= 10;
    var high = !aligned && pct > 100;
    var low = !aligned && pct < 100;

    var fillWidth = Math.max(0, Math.min(pct, 120));
    var fillColor;
    if (aligned) fillColor = 'hsl(189, 90%, 53%)';
    else if (high) fillColor = '#f59e0b';
    else fillColor = 'hsl(189, 90%, 78%)';

    var statusText;
    var statusClass;
    if (aligned) {
      statusText = 'Aligned with goal';
      statusClass = 'text-accent';
    } else if (high) {
      statusText = 'Slightly high';
      statusClass = 'text-amber-600';
    } else {
      statusText = 'Slightly low';
      statusClass = 'text-accent/70';
    }

    // Minimal right-side numeric reference
    var reference = null;
    if (high) reference = '+' + delta + unit + ' high';
    else if (low) reference = delta + unit + ' low';

    return (
      <div data-testid={'macro-plan-bar-' + macroKey}>
        {/* Top row: label + status | reference */}
        <div className="flex items-center justify-between mb-1.5 gap-2">
          <div className="flex items-baseline gap-1.5 min-w-0">
            <span className="text-[13px] font-semibold">{label}</span>
            <span className="text-[11px] text-muted-foreground/60">—</span>
            <span className={'text-[12px] font-medium flex items-center gap-0.5 ' + statusClass}>
              {aligned && <Check size={12} className="text-accent" />}
              {statusText}
            </span>
          </div>
          {reference && (
            <span className={'text-[11px] font-medium shrink-0 tabular-nums ' + statusClass}>
              {reference}
            </span>
          )}
        </div>

        {/* Bar — full width = daily goal; fill = planned pct of goal (capped 120%) */}
        <div className="relative h-[10px]">
          <div className="absolute inset-0 rounded-full" style={{ backgroundColor: 'hsl(var(--muted-foreground) / 0.18)' }} />
          {fillWidth > 0.5 && (
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
              style={{
                width: Math.min(fillWidth, 100) + '%',
                backgroundColor: fillColor
              }}
            />
          )}
          {/* Visual tick at 100% (goal end) when the fill is shorter than the bar */}
          {fillWidth < 100 && (
            <div
              className="absolute inset-y-0"
              style={{
                left: '100%',
                width: '2px',
                backgroundColor: 'hsl(var(--muted-foreground) / 0.4)',
                transform: 'translateX(-100%)'
              }}
            />
          )}
        </div>

        {/* Spacer to match progress-mode rhythm (no labels/value below in plan mode) */}
        <div className="h-1" />
      </div>
    );
  }

  // === progress mode (original Home behavior) ===
  var goal = props.goalVal || 0;
  var loggedVal = props.loggedVal || 0;
  var pctRange = (props.onTrackRange && props.onTrackRange[macroKey]) || [90, 110];
  var rangeMin = Math.round(goal * pctRange[0] / 100);
  var rangeMax = Math.round(goal * pctRange[1] / 100);
  var current = Math.round(loggedVal);
  var pr = computeProgress(current, rangeMin, rangeMax);

  var guidance;
  var guidanceClass = 'text-muted-foreground';
  if (pr.isBelow) {
    guidance = '+' + (rangeMin - current) + unit + ' to range';
    guidanceClass = 'text-muted-foreground';
  } else if (pr.isWithin) {
    guidance = '+' + (rangeMax - current) + unit + ' until max';
    guidanceClass = 'text-accent/80';
  } else {
    guidance = (current - rangeMax) + unit + ' over';
    guidanceClass = 'text-amber-600';
  }

  var statusText2;
  var statusClass2 = 'text-muted-foreground';
  if (pr.isWithin) {
    statusText2 = 'Within range';
    statusClass2 = 'text-accent';
  } else if (pr.isBelow) {
    statusText2 = (rangeMin - current) + unit + ' under';
    statusClass2 = 'text-muted-foreground';
  } else {
    statusText2 = (current - rangeMax) + unit + ' over';
    statusClass2 = 'text-amber-600';
  }

  var markerColor = pr.isWithin ? 'hsl(189, 90%, 53%)' : pr.isAbove ? '#f59e0b' : 'hsl(189, 90%, 78%)';
  var fillColorP;
  if (pr.isWithin) fillColorP = 'hsl(189, 90%, 53%)';
  else if (pr.isAbove) fillColorP = '#f59e0b';
  else fillColorP = 'hsl(189, 90%, 78%)';

  return (
    <div data-testid={'macro-bar-' + macroKey}>
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <div className="flex items-baseline gap-1.5 min-w-0">
          <span className="text-[13px] font-semibold">{label}</span>
          <span className="text-[11px] text-muted-foreground/60">—</span>
          <span className={'text-[12px] font-medium flex items-center gap-0.5 ' + statusClass2}>
            {pr.isWithin && <Check size={12} className="text-accent" />}
            {statusText2}
          </span>
        </div>
        <span className={'text-[11px] font-medium shrink-0 ' + guidanceClass}>
          {guidance}
        </span>
      </div>

      <div className="relative h-[10px]">
        <div className="absolute inset-0 rounded-full" style={{ backgroundColor: 'hsl(var(--muted-foreground) / 0.18)' }} />
        {pr.markerPct > 0.5 && (
          <div
            className="absolute inset-y-0 left-0 rounded-l-full transition-all duration-700 ease-out"
            style={{ width: pr.markerPct + '%', backgroundColor: fillColorP }}
          />
        )}
        <div className="absolute inset-y-0" style={{ left: ZONE_L + '%', width: '2px', backgroundColor: 'hsl(var(--muted-foreground) / 0.75)', transform: 'translateX(-50%)' }} />
        <div className="absolute inset-y-0" style={{ left: ZONE_R + '%', width: '2px', backgroundColor: 'hsl(var(--muted-foreground) / 0.75)', transform: 'translateX(-50%)' }} />
        <div className="absolute inset-y-0" style={{ left: (ZONE_L + ZONE_W / 2) + '%', width: '0px', borderLeft: '1.5px dashed hsl(var(--accent) / 0.9)', transform: 'translateX(-50%)' }} />
        <div
          className="absolute rounded-full transition-all duration-700 ease-out"
          style={{ left: 'calc(' + pr.markerPct + '% - 2px)', top: '-3px', bottom: '-3px', width: '4px', backgroundColor: markerColor, boxShadow: '0 0 5px ' + markerColor + '50' }}
        />
        <span className="absolute text-[9px] uppercase tracking-wider text-muted-foreground/70 pointer-events-none" style={{ left: ZONE_L + '%', top: '100%', marginTop: '4px', transform: 'translateX(-50%)' }}>min</span>
        <span className="absolute text-[9px] uppercase tracking-wider text-accent/80 pointer-events-none" style={{ left: (ZONE_L + ZONE_W / 2) + '%', top: '100%', marginTop: '4px', transform: 'translateX(-50%)' }}>goal</span>
        <span className="absolute text-[9px] uppercase tracking-wider text-muted-foreground/70 pointer-events-none" style={{ left: ZONE_R + '%', top: '100%', marginTop: '4px', transform: 'translateX(-50%)' }}>max</span>
        <span
          className="absolute text-[11px] font-bold tabular-nums"
          style={{ left: pr.markerPct + '%', top: '100%', marginTop: '16px', transform: 'translateX(-50%)', color: markerColor, whiteSpace: 'nowrap' }}
        >
          {current}{unit}
        </span>
      </div>

      <div className="h-8" />
    </div>
  );
}

export default MacroBar;
